/**
 * Apply EditReportWithAI operations (normalized by editReportAiContract.js)
 * to report HTML. Pure: HTML string in, HTML string out.
 *
 * Text is matched inside one block (p, li, heading, cell…) across inline
 * marks (bold, spans), with whitespace collapsed, so Hebrew/RTL text and
 * formatting are kept. Nothing is guessed: an operation whose text is not
 * found, or matches more than once where that is unsafe, is skipped and
 * returned in `skipped` with a reason.
 */

const BLOCK_SELECTOR = "p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, pre, div";
const HEADING_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6"]);
const WS = /[\s ‏‎]/;

export const SKIP_REASON = {
  NOT_FOUND: "not_found",
  AMBIGUOUS: "ambiguous",
  SECTION_NOT_FOUND: "section_not_found",
  UNKNOWN_DESTINATION: "unknown_destination",
  UNSUPPORTED: "unsupported",
  // The op is marked preserveHeadings but would remove a section heading.
  HEADINGS_REMOVED: "headings_removed",
  // Soft: nothing to do (e.g. replaceAll with no match). Not shown as a failure.
  NO_MATCH: "no_match",
  NOTHING_TO_FORMAT: "nothing_to_format",
};

export const SOFT_SKIP_REASONS = new Set([SKIP_REASON.NO_MATCH, SKIP_REASON.NOTHING_TO_FORMAT]);

function normalizeNeedle(text) {
  return String(text || "")
    .replace(/[\s ‏‎]+/g, " ")
    .trim();
}

/** Leaf blocks: block elements that contain no other block element. */
function leafBlocks(root) {
  return Array.from(root.querySelectorAll(BLOCK_SELECTOR)).filter(
    (el) => !el.querySelector(BLOCK_SELECTOR),
  );
}

/**
 * Character index of a block: raw text (text nodes, <br> as a space) with a
 * whitespace-collapsed view and a map back to raw positions.
 */
function indexBlock(block) {
  const segs = [];
  let raw = "";
  const walk = (node) => {
    for (let child = node.firstChild; child; child = child.nextSibling) {
      if (child.nodeType === 3) {
        segs.push({ node: child, start: raw.length, end: raw.length + child.data.length });
        raw += child.data;
      } else if (child.nodeType === 1) {
        if (child.tagName === "BR") {
          segs.push({ br: child, start: raw.length, end: raw.length + 1 });
          raw += " ";
        } else {
          walk(child);
        }
      }
    }
  };
  walk(block);

  let norm = "";
  const map = [];
  let lastWasSpace = true; // trims leading whitespace
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];
    if (WS.test(ch)) {
      if (!lastWasSpace) {
        norm += " ";
        map.push(i);
        lastWasSpace = true;
      }
    } else {
      norm += ch;
      map.push(i);
      lastWasSpace = false;
    }
  }
  if (norm.endsWith(" ")) {
    norm = norm.slice(0, -1);
    map.pop();
  }
  return { block, segs, raw, norm, map };
}

function findIn(haystack, needle, from, exact) {
  const at = haystack.indexOf(needle, from);
  if (at >= 0 || exact) return at;
  const lowerH = haystack.toLowerCase();
  const lowerN = needle.toLowerCase();
  if (lowerH.length !== haystack.length || lowerN.length !== needle.length) return -1;
  return lowerH.indexOf(lowerN, from);
}

function sameText(a, b, exact) {
  return a === b || (!exact && a.toLowerCase() === b.toLowerCase());
}

function isHtml(value) {
  return /<[a-z][\s\S]*>/i.test(String(value || ""));
}

/**
 * The API sends `find` either as plain text or as the exact HTML of one or
 * more report blocks (e.g. `<h4>…</h4><p>…</p>`). Returns the block texts.
 */
function parseFind(doc, find) {
  if (!isHtml(find)) return { html: false, text: normalizeNeedle(find) };
  const holder = doc.createElement("div");
  holder.innerHTML = String(find);
  const blockTexts = leafBlocks(holder)
    .map((b) => normalizeNeedle(b.textContent))
    .filter(Boolean);
  if (!blockTexts.length) return { html: false, text: normalizeNeedle(holder.textContent) };
  return { html: true, blockTexts, text: blockTexts.join(" ") };
}

/** All text matches per block → [{ block, rs, re }] (raw positions). */
function findTextMatches(blocks, needle, exact) {
  const n = normalizeNeedle(needle);
  if (!n) return [];
  const out = [];
  for (const block of blocks) {
    const idx = indexBlock(block);
    let from = 0;
    for (;;) {
      const at = findIn(idx.norm, n, from, exact);
      if (at < 0) break;
      out.push({ block, rs: idx.map[at], re: idx.map[at + n.length - 1] + 1, whole: idx.norm.length === n.length });
      from = at + n.length;
    }
  }
  return out;
}

/** Consecutive blocks whose texts equal `texts` one by one. */
function findBlockSequence(blocks, texts, exact) {
  for (let i = 0; i + texts.length <= blocks.length; i += 1) {
    let ok = true;
    for (let k = 0; k < texts.length && ok; k += 1) {
      ok = sameText(indexBlock(blocks[i + k]).norm, texts[k], exact);
    }
    if (ok) return blocks.slice(i, i + texts.length);
  }
  return null;
}

/** Consecutive whole blocks whose joined text equals `needle` (plain multi-paragraph find). */
function findBlockRun(blocks, needle, exact) {
  const n = normalizeNeedle(needle);
  for (let i = 0; i < blocks.length; i += 1) {
    let joined = "";
    for (let j = i; j < blocks.length; j += 1) {
      const t = indexBlock(blocks[j]).norm;
      joined = joined ? `${joined} ${t}` : t;
      if (joined.length > n.length) break;
      if (j > i && sameText(joined, n, exact)) return blocks.slice(i, j + 1);
    }
  }
  return null;
}

/** Blocks to search: the op's section when it is found, else the whole report. */
function scopeBlocks(root, section) {
  if (section) {
    const sec = sectionBlocks(root, section);
    if (sec) return [sec.heading, ...sec.blocks];
  }
  return leafBlocks(root);
}

/**
 * Locate `find` → { blocks } (whole blocks) or { matches } (text inside blocks), or null.
 * With a section, only that section is searched (Jacky #3); the whole report
 * is used only when the section heading does not exist.
 */
function locate(root, find, { section, exact } = {}) {
  const f = parseFind(root.ownerDocument, find);
  if (!f.text) return null;
  const scopes = [scopeBlocks(root, section)];
  for (const blocks of scopes) {
    if (f.html) {
      const seq = findBlockSequence(blocks, f.blockTexts, exact);
      if (seq) return { blocks: seq, text: f.text };
    }
    const matches = findTextMatches(blocks, f.text, exact);
    if (matches.length) return { matches, text: f.text };
    const run = findBlockRun(blocks, f.text, exact);
    if (run) return { blocks: run, text: f.text };
  }
  return null;
}

function replaceRange(block, rs, re, text) {
  const { segs } = indexBlock(block);
  const doc = block.ownerDocument;

  if (rs === re) {
    // Pure insertion: prefer a text node that contains the position.
    const seg =
      segs.find((s) => s.node && s.start <= rs && rs <= s.end) ||
      segs.find((s) => s.start <= rs && rs <= s.end);
    if (!seg) {
      block.appendChild(doc.createTextNode(text));
      return;
    }
    if (seg.node) {
      const local = rs - seg.start;
      seg.node.data = seg.node.data.slice(0, local) + text + seg.node.data.slice(local);
    } else {
      const t = doc.createTextNode(text);
      if (rs === seg.start) seg.br.parentNode.insertBefore(t, seg.br);
      else seg.br.parentNode.insertBefore(t, seg.br.nextSibling);
    }
    return;
  }

  let inserted = false;
  for (const seg of segs) {
    if (seg.end <= rs || seg.start >= re) continue;
    if (seg.br) {
      if (!inserted && text) {
        seg.br.parentNode.insertBefore(doc.createTextNode(text), seg.br);
      }
      inserted = true;
      seg.br.parentNode.removeChild(seg.br);
      continue;
    }
    const a = Math.max(rs, seg.start) - seg.start;
    const b = Math.min(re, seg.end) - seg.start;
    const d = seg.node.data;
    seg.node.data = d.slice(0, a) + (inserted ? "" : text) + d.slice(b);
    inserted = true;
  }
  cleanupEmptyInline(block);
}

/**
 * Replace only the part that differs, so formatting on the unchanged start and
 * end of the matched text (e.g. a bold word) is kept.
 */
function replaceMinimal(block, rs, re, text) {
  const oldText = indexBlock(block).raw.slice(rs, re);
  let p = 0;
  while (p < oldText.length && p < text.length && oldText[p] === text[p]) p += 1;
  let q = 0;
  while (
    q < oldText.length - p &&
    q < text.length - p &&
    oldText[oldText.length - 1 - q] === text[text.length - 1 - q]
  ) {
    q += 1;
  }
  replaceRange(block, rs + p, re - q, text.slice(p, text.length - q));
}

function cleanupEmptyInline(block) {
  const walker = block.ownerDocument.createTreeWalker(block, 4 /* SHOW_TEXT */);
  const empty = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.data === "") empty.push(n);
  }
  empty.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
  Array.from(block.querySelectorAll("span, strong, b, em, i, u, s, mark"))
    .reverse()
    .forEach((el) => {
      if (!el.textContent && !el.querySelector("br, img")) el.parentNode.removeChild(el);
    });
}

/** After a deletion: collapse doubled spaces, drop space before punctuation, remove empty blocks. */
function tidyBlock(block) {
  const walker = block.ownerDocument.createTreeWalker(block, 4);
  const nodes = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    n.data = n.data.replace(/ {2,}/g, " ").replace(/ ([.,;:])/g, "$1");
    nodes.push(n);
  }
  if (nodes.length) {
    nodes[0].data = nodes[0].data.replace(/^ +/, "");
    nodes[nodes.length - 1].data = nodes[nodes.length - 1].data.replace(/ +$/, "");
  }
  if (!/[\p{L}\p{N}]/u.test(block.textContent) && !block.querySelector("img")) {
    removeBlock(block);
  }
}

/** True when `parent` has content other than `child` (ignoring whitespace text). */
function hasOtherContent(parent, child) {
  for (let c = parent.firstChild; c; c = c.nextSibling) {
    if (c !== child && !(c.nodeType === 3 && !c.data.trim())) return true;
  }
  return false;
}

/**
 * The outermost element that would be left empty without `block` (li inside
 * ul/ol, paragraph inside li…), or `block` itself.
 */
function outermostEmptyWrapper(block) {
  let el = block;
  while (el.parentNode && el.parentNode.nodeType === 1 && el.parentNode.tagName !== "BODY") {
    if (hasOtherContent(el.parentNode, el)) break;
    el = el.parentNode;
  }
  return el;
}

function removeBlock(block) {
  const el = outermostEmptyWrapper(block);
  if (el.parentNode) el.parentNode.removeChild(el);
}

function headingLevel(block) {
  if (HEADING_TAGS.has(block.tagName)) return Number(block.tagName.slice(1));
  const text = normalizeNeedle(block.textContent);
  if (!text || text.length > 60) return 0;
  // CRE templates often use a bold-only paragraph as a section title.
  const walker = block.ownerDocument.createTreeWalker(block, 4);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!n.data.trim()) continue;
    if (!n.parentElement || !n.parentElement.closest("strong, b")) return 0;
  }
  return 7;
}

function isHeadingLike(block) {
  return headingLevel(block) > 0;
}

function sectionKey(text) {
  return normalizeNeedle(text).replace(/[:：.\-–]+$/u, "").trim().toLowerCase();
}

/**
 * Blocks of the section whose heading matches `section` (e.g. "FINDINGS"
 * matches <h3>FINDINGS:</h3>). The section runs until the next heading of the
 * same or a higher level, so sub-headings (h4 under h3) belong to it.
 * Exact title matches win over partial ones.
 */
function sectionBlocks(root, section) {
  const want = sectionKey(section);
  if (!want) return null;
  const blocks = leafBlocks(root);
  const headings = blocks
    .map((b, i) => ({ b, i, level: headingLevel(b), key: sectionKey(b.textContent) }))
    .filter((h) => h.level > 0 && h.key);
  const hit =
    headings.find((h) => h.key === want) ||
    headings.find((h) => h.key.includes(want) || want.includes(h.key));
  if (!hit) return null;
  const out = [];
  for (let i = hit.i + 1; i < blocks.length; i += 1) {
    const level = headingLevel(blocks[i]);
    if (level && level <= hit.level) break;
    out.push(blocks[i]);
  }
  return { heading: hit.b, blocks: out };
}

const UNSAFE_TAGS = "script, style, iframe, object, embed, link, meta, form, input, button";

/** AI text → DOM nodes. Plain text becomes one <p>; HTML is parsed and cleaned. */
function contentNodes(doc, text, likeBlock) {
  const value = String(text || "").trim();
  const holder = doc.createElement("div");
  if (/<[a-z][\s\S]*>/i.test(value)) {
    holder.innerHTML = value;
    holder.querySelectorAll(UNSAFE_TAGS).forEach((el) => el.remove());
    holder.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (/^on/i.test(attr.name) || /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
      });
    });
  } else {
    holder.textContent = value;
  }
  // Loose inline content (text, <strong>…) is wrapped in one paragraph.
  const nodes = [];
  let loose = null;
  Array.from(holder.childNodes).forEach((node) => {
    const isBlock = node.nodeType === 1 && node.matches(`${BLOCK_SELECTOR}, ul, ol, table`);
    if (isBlock) {
      loose = null;
      nodes.push(node);
    } else if (node.nodeType === 1 || node.textContent.trim()) {
      if (!loose) {
        loose = doc.createElement("p");
        nodes.push(loose);
      }
      loose.appendChild(node);
    }
  });
  // New paragraphs take the language/direction of the report text around them.
  const lang = likeBlock && likeBlock.getAttribute("lang");
  const dir = likeBlock && likeBlock.getAttribute("dir");
  nodes.forEach((node) => {
    if (node.tagName !== "P" && !HEADING_TAGS.has(node.tagName)) return;
    if (lang && !node.hasAttribute("lang")) node.setAttribute("lang", lang);
    if (dir && !node.hasAttribute("dir")) node.setAttribute("dir", dir);
  });
  return nodes;
}

/** Plain text of AI content (for inline inserts). */
function plainText(doc, text) {
  const holder = doc.createElement("div");
  holder.innerHTML = String(text || "");
  return normalizeNeedle(holder.textContent);
}

/** Insert nodes after (or before) `block`; inside a list a new paragraph becomes a new item. */
function insertNodes(block, nodes, where = "after") {
  const doc = block.ownerDocument;
  const li = block.tagName === "LI" ? block : block.parentElement && block.parentElement.closest("li");
  let ref = li || block;
  const wrap = (node) => {
    if (!li || node.tagName === "LI") return node;
    const item = doc.createElement("li");
    item.appendChild(node);
    return item;
  };
  // Inside a list, a list returned by the AI (<ol><li>…</li></ol>) is merged
  // item by item instead of being nested as a list inside a new item.
  if (li) {
    nodes = nodes.flatMap((node) =>
      node.tagName === "OL" || node.tagName === "UL"
        ? Array.from(node.children).filter((c) => c.tagName === "LI")
        : [node],
    );
  }
  if (where === "before") {
    nodes.forEach((node) => ref.parentNode.insertBefore(wrap(node), ref));
    return;
  }
  for (const node of nodes) {
    const el = wrap(node);
    ref.parentNode.insertBefore(el, ref.nextSibling);
    ref = el;
  }
}

function insertNodesAfter(block, nodes) {
  insertNodes(block, nodes, "after");
}

/** Take a block out of the report, removing list wrappers it leaves empty. */
function detachBlock(block) {
  const el = outermostEmptyWrapper(block);
  if (el !== block && el.parentNode) el.parentNode.removeChild(el);
  else if (block.parentNode) block.parentNode.removeChild(block);
  return block;
}

/**
 * Section named in a free-text rule ("…in the FINDINGS section…"): the one
 * report heading whose title appears in the rule, or null.
 */
function sectionFromRule(root, rule) {
  const text = normalizeNeedle(rule).toLowerCase();
  if (!text) return null;
  const keys = new Set(
    leafBlocks(root)
      .filter(isHeadingLike)
      .map((b) => sectionKey(b.textContent))
      .filter((k) => k && text.includes(k)),
  );
  return keys.size === 1 ? Array.from(keys)[0] : null;
}

function joinText(before, text, after) {
  const needLead = before && !WS.test(before[before.length - 1]) && !/^[.,;:)]/.test(text);
  const needTrail = after && !WS.test(after[0]) && !/^[.,;:)]/.test(after);
  return `${needLead ? " " : ""}${text}${needTrail ? " " : ""}`;
}

// ── operations ────────────────────────────────────────────────────────────

function replaceBlocks(doc, blocks, html) {
  const nodes = contentNodes(doc, html, blocks[0]);
  insertNodesAfter(blocks[blocks.length - 1], nodes);
  blocks.forEach(removeBlock);
}

function applyReplace(root, op) {
  const doc = root.ownerDocument;
  const loc = locate(root, op.find, op);
  if (!loc) return op.softMiss ? SKIP_REASON.NO_MATCH : SKIP_REASON.NOT_FOUND;
  if (loc.blocks) {
    replaceBlocks(doc, loc.blocks, op.replace);
    return null;
  }
  const chosen = op.all ? loc.matches : [loc.matches[0]];
  // Whole paragraph replaced by HTML blocks (e.g. one <p> → two <p>).
  if (isHtml(op.replace) && chosen.length === 1 && chosen[0].whole) {
    replaceBlocks(doc, [chosen[0].block], op.replace);
    return null;
  }
  const text = isHtml(op.replace) ? plainText(doc, op.replace) : op.replace;
  // Right to left so earlier positions in the same block stay valid.
  for (const m of chosen.slice().reverse()) replaceMinimal(m.block, m.rs, m.re, text);
  if (!text) chosen.forEach((m) => tidyBlock(m.block));
  return null;
}

function applyDelete(root, op) {
  if (op.wholeSection) {
    const sec = sectionBlocks(root, op.section);
    if (!sec) return SKIP_REASON.SECTION_NOT_FOUND;
    // preserveHeadings: clear the section's content but keep its title.
    if (op.preserveHeadings && !sec.blocks.length) return SKIP_REASON.NO_MATCH;
    sec.blocks.forEach(removeBlock);
    if (!op.preserveHeadings) removeBlock(sec.heading);
    return null;
  }
  const loc = locate(root, op.find, op);
  if (!loc) return op.softMiss ? SKIP_REASON.NO_MATCH : SKIP_REASON.NOT_FOUND;
  if (loc.blocks) {
    loc.blocks.forEach(removeBlock);
    return null;
  }
  const chosen = op.all ? loc.matches : [loc.matches[0]];
  for (const m of chosen.slice().reverse()) replaceRange(m.block, m.rs, m.re, "");
  chosen.forEach((m) => tidyBlock(m.block));
  return null;
}

function applyAppend(root, op) {
  const doc = root.ownerDocument;
  const all = leafBlocks(root);
  const like = all.find((b) => b.tagName === "P") || null;
  if (op.section) {
    const sec = sectionBlocks(root, op.section);
    if (!sec) return SKIP_REASON.SECTION_NOT_FOUND;
    const body = sec.blocks.filter((b) => !isHeadingLike(b));
    const last = sec.blocks[sec.blocks.length - 1];
    // Section with sub-sections (FINDINGS > h4 …): the end of the section is
    // inside its last sub-section, so text appended "to FINDINGS" would read
    // as part of that sub-section (live 22 Oct). The API names a sub-section
    // when the text belongs to one; otherwise it is a general finding and goes
    // right under the section heading, after any text before the first sub-heading.
    const firstSub = sec.blocks.findIndex(isHeadingLike);
    if (firstSub >= 0) {
      const intro = sec.blocks.slice(0, firstSub);
      const after = op.atStart || !intro.length ? sec.heading : intro[intro.length - 1];
      insertNodesAfter(after, contentNodes(doc, op.text, intro[intro.length - 1] || body[0] || like || sec.heading));
      return null;
    }
    if (op.atStart || !body.length) {
      insertNodesAfter(sec.heading, contentNodes(doc, op.text, body[0] || like || sec.heading));
      return null;
    }
    return appendAfter(doc, last, op, body[body.length - 1]);
  }
  const last = all[all.length - 1];
  if (!last) {
    contentNodes(doc, op.text, null).forEach((n) => root.appendChild(n));
    return null;
  }
  return appendAfter(doc, last, op, like);
}

function appendAfter(doc, last, op, like) {
  // Always a NEW paragraph / list item (agreed with Vigen, 21 Oct), never
  // concatenated into the last block.
  insertNodesAfter(last, contentNodes(doc, op.text, like || last));
  return null;
}

/** Text with block HTML (<p>, <li>, <ol>…) is inserted as new blocks, not inline. */
function hasBlockHtml(text) {
  return /<(p|h[1-6]|li|ol|ul|div|table|blockquote)[\s>]/i.test(String(text || ""));
}

function insertAt(root, anchor, position, text, opts = {}) {
  const loc = locate(root, anchor, opts);
  if (!loc) return SKIP_REASON.NOT_FOUND;
  if (!loc.blocks && loc.matches.length > 1) return SKIP_REASON.AMBIGUOUS;
  const blocks = loc.blocks || (hasBlockHtml(text) ? [loc.matches[0].block] : null);
  if (blocks) {
    insertNodes(position === "before" ? blocks[0] : blocks[blocks.length - 1],
      contentNodes(root.ownerDocument, text, blocks[0]), position);
    return null;
  }
  text = plainText(root.ownerDocument, text);
  const m = loc.matches[0];
  const idx = indexBlock(m.block);
  const at = position === "before" ? m.rs : m.re;
  replaceRange(m.block, at, at, joinText(idx.raw.slice(0, at), text, idx.raw.slice(at)));
  return null;
}

function applyInsert(root, op) {
  return insertAt(root, op.anchor, op.position, op.text, op);
}

/**
 * move_text. Source: `find` (text or block HTML). Destination (normalized by
 * the contract from the API's `rule` + `targetSectionId`):
 *   { where: "section_start" | "section_end", section } or { where: "after" | "before", anchor }.
 */
function applyMove(root, op) {
  const doc = root.ownerDocument;
  const dest = op.destination || {};
  const loc = locate(root, op.find, op);
  if (!loc) return SKIP_REASON.NOT_FOUND;
  let moving = loc.blocks || null;
  if (!moving) {
    if (loc.matches.length > 1) return SKIP_REASON.AMBIGUOUS;
    if (loc.matches[0].whole) moving = [loc.matches[0].block];
  }

  // Resolve the destination before changing anything.
  let ref;
  let where;
  if (dest.where === "after" || dest.where === "before") {
    const anchor = locate(root, dest.anchor, {});
    if (!anchor) return SKIP_REASON.NOT_FOUND;
    if (anchor.matches && anchor.matches.length > 1) return SKIP_REASON.AMBIGUOUS;
    const anchorBlocks = anchor.blocks || [anchor.matches[0].block];
    if (!moving) {
      // Part of a sentence moved next to other text: plain text insert.
      const m = loc.matches[0];
      const moved = normalizeNeedle(indexBlock(m.block).raw.slice(m.rs, m.re));
      replaceRange(m.block, m.rs, m.re, "");
      tidyBlock(m.block);
      return insertAt(root, dest.anchor, dest.where, moved);
    }
    ref = dest.where === "before" ? anchorBlocks[0] : anchorBlocks[anchorBlocks.length - 1];
    where = dest.where;
  } else if (dest.where === "section_start" || dest.where === "section_end") {
    const name = dest.section || op.section || sectionFromRule(root, op.rule);
    const sec = name && sectionBlocks(root, name);
    if (!sec) return SKIP_REASON.SECTION_NOT_FOUND;
    if (dest.where === "section_start") {
      ref = sec.heading;
    } else {
      const rest = sec.blocks.filter((b) => !(moving || []).includes(b));
      ref = rest[rest.length - 1] || sec.heading;
    }
    where = "after";
  } else {
    return SKIP_REASON.UNKNOWN_DESTINATION;
  }
  if (moving && moving.includes(ref)) return null; // already there

  if (moving) {
    const nodes = moving.map(detachBlock);
    insertNodes(ref, nodes, where);
    return null;
  }
  const m = loc.matches[0];
  const moved = normalizeNeedle(indexBlock(m.block).raw.slice(m.rs, m.re));
  replaceRange(m.block, m.rs, m.re, "");
  tidyBlock(m.block);
  insertNodes(ref, contentNodes(doc, moved, m.block), where);
  return null;
}

/** Positions (raw) right after a sentence-ending period followed by more text. */
function sentenceBreaks(raw) {
  const out = [];
  const re = /\.(?=[\s ]+\S)/g;
  let m;
  while ((m = re.exec(raw))) {
    const before = raw.slice(0, m.index);
    if (/^\s*\d+$/.test(before)) continue; // "1. " list number
    if (/(^|\s)[A-Za-z]$/.test(before)) continue; // "e.g", initials
    let end = m.index + 1;
    while (end < raw.length && WS.test(raw[end])) end += 1;
    // Already followed by a line break: nothing to do.
    if (/\n/.test(raw.slice(m.index + 1, end))) continue;
    out.push({ at: m.index + 1, end });
  }
  return out;
}

function splitBlockAt(block, rawPos) {
  // Split the block into two siblings at a raw position (paragraph mode).
  const doc = block.ownerDocument;
  const { segs } = indexBlock(block);
  const seg = segs.find((s) => s.node && s.start <= rawPos && rawPos <= s.end);
  if (!seg) return;
  const range = doc.createRange();
  range.setStart(seg.node, rawPos - seg.start);
  range.setEnd(block, block.childNodes.length);
  const tail = range.extractContents();
  const clone = block.cloneNode(false);
  clone.appendChild(tail);
  block.parentNode.insertBefore(clone, block.nextSibling);
}

function applyFormatSentences(root, op) {
  let blocks;
  // scope "section" may name the section only inside the rule text.
  const name = op.section || (op.scope === "section" ? sectionFromRule(root, op.rule) : null);
  if (name) {
    const sec = sectionBlocks(root, name);
    if (!sec) return SKIP_REASON.SECTION_NOT_FOUND;
    blocks = sec.blocks;
  } else if (op.scope === "section") {
    return SKIP_REASON.SECTION_NOT_FOUND;
  } else {
    blocks = leafBlocks(root);
  }
  blocks = blocks.filter((b) => !isHeadingLike(b) && b.tagName !== "PRE");
  let changed = 0;
  for (const block of blocks) {
    const breaks = sentenceBreaks(indexBlock(block).raw);
    for (const br of breaks.slice().reverse()) {
      replaceRange(block, br.at, br.end, "");
      if (op.mode === "paragraph") {
        splitBlockAt(block, br.at);
      } else {
        const { segs } = indexBlock(block);
        const seg = segs.find((x) => x.node && x.start < br.at && br.at <= x.end);
        if (!seg) continue;
        const rest = seg.node.splitText(br.at - seg.start);
        rest.parentNode.insertBefore(block.ownerDocument.createElement("br"), rest);
      }
      changed += 1;
    }
    cleanupEmptyInline(block);
  }
  return changed ? null : SKIP_REASON.NOTHING_TO_FORMAT;
}

function headingTexts(root) {
  return Array.from(root.querySelectorAll("h1, h2, h3, h4, h5, h6")).map((h) => sectionKey(h.textContent));
}

/** True when a heading present before is gone (counts duplicates). */
function lostHeading(before, after) {
  const left = new Map();
  after.forEach((k) => left.set(k, (left.get(k) || 0) + 1));
  return before.some((k) => {
    const n = left.get(k) || 0;
    if (!n) return true;
    left.set(k, n - 1);
    return false;
  });
}

const HANDLERS = {
  replace: applyReplace,
  delete: applyDelete,
  append: applyAppend,
  insert: applyInsert,
  move: applyMove,
  format_sentences: applyFormatSentences,
};

/**
 * @param {string} html report HTML (clean: no AI diff markup)
 * @param {Array<object>} operations normalized operations
 * @returns {{ html: string, applied: object[], skipped: Array<{ op: object, reason: string }> }}
 */
export function applyEditOperations(html, operations) {
  const doc = new DOMParser().parseFromString(`<body>${html || ""}</body>`, "text/html");
  const root = doc.body;
  const applied = [];
  const skipped = [];
  for (const op of operations || []) {
    const handler = HANDLERS[op && op.kind];
    if (!handler) {
      skipped.push({ op, reason: SKIP_REASON.UNSUPPORTED });
      continue;
    }
    const snapshot = root.innerHTML;
    const headingsBefore = op.preserveHeadings ? headingTexts(root) : null;
    let reason;
    try {
      reason = handler(root, op);
    } catch (err) {
      reason = SKIP_REASON.UNSUPPORTED;
    }
    // Live (21 Oct): a vague "fix this" came back as a whole-report replace
    // that dropped three sections while claiming preserveHeadings: true.
    if (!reason && headingsBefore && lostHeading(headingsBefore, headingTexts(root))) {
      reason = SKIP_REASON.HEADINGS_REMOVED;
    }
    if (reason) root.innerHTML = snapshot;
    if (reason) skipped.push({ op, reason });
    else applied.push(op);
  }
  return { html: applied.length ? root.innerHTML : html, applied, skipped };
}
